/**
 * GA4 + dataLayer instrumentation for the branded error pages.
 *
 * Fires three signals so a 404 / 500 is impossible to miss in reporting:
 *   1. dataLayer push  -> GTM-friendly, picked up even if gtag failed to load
 *   2. gtag 'exception' -> GA4's standard error event, surfaces in Reports
 *   3. custom event   -> e.g. page_not_found, page_error, page_global_error
 *                          (easier to filter in GA4 than the generic exception)
 */

type ErrorKind = 'not_found' | 'site_error' | 'global_error'

const customEventName: Record<ErrorKind, string> = {
  not_found: 'page_not_found',
  site_error: 'page_error',
  global_error: 'page_global_error',
}

interface TrackErrorArgs {
  kind: ErrorKind
  description: string
  fatal?: boolean
  digest?: string
}

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

/**
 * Calls window.gtag() directly.
 *
 * This used to push a bare `['event', ...]` array into dataLayer instead, on
 * the theory that gtag.js, loaded lazily, would replay whatever sat in the
 * array once it initialised. Neither half of that holds: analytics-scripts.tsx
 * loads gtag.js and the init script with `strategy="afterInteractive"`, not
 * lazyOnload, so window.gtag is up well before an error page finishes
 * hydrating; and gtag.js only converts entries present at load time (or passed
 * through the gtag() function it defines) into real hits, so a push made after
 * it has already initialised is silently dropped rather than queued. The site
 * recorded no exception or page_not_found events at all while this was in
 * place, against 3,218 page views. If gtag genuinely isn't ready the event is
 * skipped rather than faking a queue that doesn't work.
 */
function queueGtag(args: unknown[]): void {
  const w = window as AnalyticsWindow
  if (typeof w.gtag !== 'function') return
  w.gtag(...args)
}

export function trackErrorPageView({
  kind,
  description,
  fatal = false,
  digest,
}: TrackErrorArgs): void {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow

  const path = window.location.pathname + window.location.search
  const referrer = document.referrer || '(direct)'
  const safeDescription = description.slice(0, 250)

  // Custom GTM-friendly object event (matches existing app analytics shape).
  w.dataLayer = w.dataLayer ?? []
  w.dataLayer.push({
    event: 'rne_error_page',
    error_kind: kind,
    error_description: safeDescription,
    error_fatal: fatal,
    error_digest: digest ?? null,
    error_path: path,
    error_referrer: referrer,
  })

  // GA4 standard exception event - shows up in Reports under exception.
  queueGtag([
    'event',
    'exception',
    {
      description: safeDescription,
      fatal,
      error_kind: kind,
      error_digest: digest ?? null,
      error_path: path,
      error_referrer: referrer,
    },
  ])

  // Custom event with a unique name so it's filterable in GA4 Realtime/Reports.
  queueGtag([
    'event',
    customEventName[kind],
    {
      error_description: safeDescription,
      error_digest: digest ?? null,
      error_path: path,
      error_referrer: referrer,
      error_fatal: fatal,
    },
  ])
}
